<template>
  <v-main>
    <v-app-bar app flat light color="white">
      <AppbarBackIcon />
      <v-toolbar-title class="font-weight-bold">
        <span v-if="isCreate">创建任务</span>
        <span v-else>数据导入任务</span>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn v-if="!isCreate" color="red" class="ml-4" outlined small @click="onDelete">删除</v-btn>
    </v-app-bar>
    <v-container fluid>
      <v-text-field
        :readonly="!isCreate"
        label="任务标题"
        hint="例如：系统升级"
        class="required"
        v-model="form.title"
      ></v-text-field>
      <v-row>
        <v-col>
          <v-select
            class="required"
            :items="equityOptionList"
            label="权益"
            v-model="form.equity_id"
            :readonly="!isCreate"
        /></v-col>
        <v-col
          ><v-select
            class="required"
            label="变更类型"
            :readonly="!isCreate"
            :items="changeTypeList"
            v-model="form.change_type"
        /></v-col>
      </v-row>
      <v-file-input
        v-if="isCreate"
        label="上传CSV文件"
        class="required"
        accept="text/*"
        show-size
        v-model="form.file"
      />
      <v-checkbox
        v-model="form.skip_initial_balance"
        :readonly="!isCreate"
        :disabled="!isCreate"
        label="不分配初始余额"
      />
      <v-text-field v-if="!isCreate && task" readonly label="任务状态" :value="statusName" />
      <v-row>
        <v-col>
          <v-text-field v-if="!isCreate && task" readonly label="数据条数" :value="task.total" />
        </v-col>
        <v-col>
          <v-text-field v-if="!isCreate && task" readonly label="已执行" :value="task.num_execute" />
        </v-col>
        <v-col>
          <v-text-field v-if="!isCreate && task" readonly label="已撤销" :value="task.num_revert" />
        </v-col>
      </v-row>
      <v-text-field v-if="!isCreate && task" readonly label="创建时间" :value="task.dt_created" />
      <v-btn v-if="isCreate" width="100%" color="primary" class="mt-8" @click="onCreate" :loading="isCreateLoading">
        <span>创建</span>
      </v-btn>
      <v-row v-if="!isCreate">
        <v-col cols="6">
          <v-btn
            :disabled="!showExecute"
            :loading="isExecuteLoading"
            width="100%"
            color="primary"
            class="mt-8"
            @click="onExecute"
            >执行</v-btn
          >
        </v-col>
        <v-col cols="6">
          <v-btn
            :disabled="!showRevert || isExecuteLoading"
            :loading="isRevertLoading"
            width="100%"
            color="secondary"
            class="mt-8"
            @click="onRevert"
            >撤销</v-btn
          >
        </v-col>
      </v-row>
      <v-dialog :value="showLoading" persistent width="300">
        <v-card color="primary" dark>
          <v-card-title class="text-center">任务正在处理中</v-card-title>
          <v-card-text>
            <p>{{ progressTitle }}</p>
            <v-progress-linear color="white" :value="progressValue" height="25">
              <strong class="progress-value">{{ Math.ceil(progressValue) }}%</strong>
            </v-progress-linear>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import _ from 'lodash'
import AppbarBackIcon from '@/components/AppbarBackIcon.vue'
import { mdiDelete } from '@mdi/js'
import { balanceTaskStore } from '@/store/balanceTask'
import { homeStore } from '@/store/home'
import { BalanceChangeType } from '@/plugins/balance'
import { BalanceTaskStatus, getBalanceTaskStatusName } from '@/plugins/balanceTask'
import { equityStore } from '@/store/equity'

const changeTypeList = [
  {
    text: '增加余额',
    value: BalanceChangeType.TASK_INCREASE,
  },
  {
    text: '减少余额',
    value: BalanceChangeType.TASK_DECREASE,
  },
]

export default {
  name: 'BalanceTaskDetailPage',
  components: { AppbarBackIcon },
  data() {
    return {
      mdiDelete,
      form: {
        title: null,
        equity_id: null,
        file: null,
        change_type: changeTypeList[0].value,
        skip_initial_balance: true,
      },
      changeTypeList,
      isCreateLoading: false,
      isExecuteLoading: false,
      isRevertLoading: false,
    }
  },
  computed: {
    id() {
      return this.$route.query.id || null
    },
    isCreate() {
      return _.isNil(this.id)
    },
    task() {
      return balanceTaskStore.get(this.id)
    },
    equityOptionList() {
      return equityStore.list.map(equity => {
        return {
          text: equity.name,
          value: equity.id,
        }
      })
    },
    statusName() {
      if (this.isCreate || _.isNil(this.task)) {
        return null
      }
      return getBalanceTaskStatusName(this.task.status)
    },
    canCreate() {
      return !_.isNil(this.form.title) && !_.isNil(this.form.file)
    },
    showExecute() {
      if (this.isCreate || _.isNil(this.task)) {
        return false
      }
      return {
        [BalanceTaskStatus.INIT]: true,
        [BalanceTaskStatus.EXECUTE]: true,
      }[this.task.status]
    },
    showRevert() {
      if (this.isCreate || _.isNil(this.task)) {
        return false
      }
      return {
        [BalanceTaskStatus.EXECUTE]: true,
        [BalanceTaskStatus.EXECUTE_DONE]: true,
        [BalanceTaskStatus.REVERT]: true,
      }[this.task.status]
    },
    showLoading() {
      return this.isExecuteLoading || this.isRevertLoading
    },
    progressTitle() {
      if (this.isExecuteLoading) {
        return `执行进度 ${this.task.num_execute}/${this.task.total}`
      }
      if (this.isRevertLoading) {
        return `撤销进度 ${this.task.num_revert}/${this.task.total}`
      }
      return ''
    },
    progressValue() {
      if (this.isExecuteLoading) {
        return (100 * this.task.num_execute) / this.task.total
      }
      if (this.isRevertLoading) {
        return (100 * this.task.num_revert) / this.task.total
      }
      return 0
    },
  },
  async mounted() {
    await homeStore.doLoad()
    await balanceTaskStore.doLoad()
    await equityStore.doLoad()
    if (!_.isNil(this.task)) {
      this.form.title = this.task.title
      this.form.equity_id = this.task.equity_id
      this.form.change_type = this.task.change_type
      this.form.skip_initial_balance = this.task.skip_initial_balance
    }
  },
  methods: {
    async onCreate() {
      if (!this.isCreate || !this.canCreate || this.isCreateLoading) {
        return
      }
      let task
      this.isCreateLoading = true
      try {
        task = await balanceTaskStore.doCreate({ ...this.form })
      } finally {
        this.isCreateLoading = false
      }
      this.$router.replace({ query: { id: task.id } })
      this.$toasted.success('创建成功')
    },
    async onDelete() {
      if (this.isCreate || _.isNil(this.task)) {
        return
      }
      const msg = `确定要删除任务「${this.task.title}」吗`
      if (!(await this.$dialog.confirm({ text: msg }))) {
        return
      }
      await balanceTaskStore.doDelete(this.id)
      this.$toasted.success('删除成功')
      this.$router.back()
    },
    async onExecute() {
      if (!this.showExecute || this.isExecuteLoading) {
        return
      }
      this.isExecuteLoading = true
      try {
        await balanceTaskStore.doExecute({ id: this.id })
      } finally {
        this.isExecuteLoading = false
      }
      this.$toasted.success('执行成功')
    },
    async onRevert() {
      if (!this.showRevert || this.isRevertLoading || this.isExecuteLoading) {
        return
      }
      this.isRevertLoading = true
      try {
        await balanceTaskStore.doRevert({ id: this.id })
      } finally {
        this.isRevertLoading = false
      }
      this.$toasted.success('撤销成功')
    },
  },
}
</script>

<style lang="less" scoped>
.progress-value {
  color: #212121;
}
</style>
